import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip } from '@progress/kendo-react-buttons';
import cn from 'clsx';
import moment from 'moment';
import editDialogStyles from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import styles from '@components-clean/core/drag-drop-lists-core/drag-drop-item/style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { ChipProps, RAGPillColours } from '@components-clean/core/grid/custom-cell/chip-constants';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { ObservationType } from '@interfaces-clean/observations.interface';
import { NamedItemType } from '@interfaces-clean/organisationLov.interface';
import { ControlsReviewLog } from '@interfaces-clean/review-logs.interface';
import { ObservationDetails } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation-details';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';
import OrganisationLovApi from '@apis/bff-api/central-monitoring/organisationLovs';

interface Props {
    reviewLog: ControlsReviewLog;
    studyRef: string | null;
    onSubmit: (data: EditReviewLogForm) => void;
    onClose: () => void;
}

export interface EditReviewLogForm {
    dataset: Date | null;
    association: string | null;
    panel: string | null;
    decision: { id: number; name: string; groupedBy: string; disabled: boolean; active: boolean } | null;
    comments: string | null;
    created_date: Date | null;
    created_by: string;
    updated_date: Date | null;
    updated_by: string | null;
    reviewLogId: number;
}

// TODO this was stolen review-log-grid-helper.ts - which uses chip-constants - this will be refactored into utils clean in another pr
// for both grid and this
const statusRAGChips: ChipProps[] = [
    {
        text: 'Upper Red',
        value: 'Upper Red',
        backgroundColor: RAGPillColours.ragRed,
        borderColor: RAGPillColours.ragRedBorder,
    },
    {
        text: 'Upper Amber',
        value: 'Upper Amber',
        backgroundColor: RAGPillColours.ragAmber,
        borderColor: RAGPillColours.ragAmberBorder,
    },
    {
        text: 'Lower Amber',
        value: 'Lower Amber',
        backgroundColor: RAGPillColours.ragAmber,
        borderColor: RAGPillColours.ragAmberBorder,
    },
    {
        text: 'Lower Red',
        value: 'Lower Red',
        backgroundColor: RAGPillColours.ragRed,
        borderColor: RAGPillColours.ragRedBorder,
    },
    {
        text: 'Below Min Data',
        value: 'Below Min Data',
        backgroundColor: RAGPillColours.ragGrey,
        borderColor: RAGPillColours.ragGreyBorder,
    },
    {
        text: 'Green',
        value: 'Green',
        backgroundColor: RAGPillColours.ragGreen,
        borderColor: RAGPillColours.ragGreenBorder,
    },
];

// TODO this was stolen review-log-grid-helper.ts - which uses chip-constants - this will be refactored into utils clean in another pr
// for both grid and this
const ootRAGChips: ChipProps[] = [
    {
        text: 'None',
        value: 'None',
        backgroundColor: RAGPillColours.ragNone,
        borderColor: RAGPillColours.ragNoneBorder,
    },
    {
        text: 'Upper Red',
        value: 'Upper Red',
        backgroundColor: RAGPillColours.ragRed,
        borderColor: RAGPillColours.ragRedBorder,
    },
    {
        text: 'Lower Red',
        value: 'Lower Red',
        backgroundColor: RAGPillColours.ragRed,
        borderColor: RAGPillColours.ragRedBorder,
    },
    {
        text: 'Upper Amber',
        value: 'Upper Amber',
        backgroundColor: RAGPillColours.ragAmber,
        borderColor: RAGPillColours.ragAmberBorder,
    },
    {
        text: 'Lower Amber',
        value: 'Lower Amber',
        backgroundColor: RAGPillColours.ragAmber,
        borderColor: RAGPillColours.ragAmberBorder,
    },
];

// NOTE - this is a copy of the Study Observations Review Log -
// the only difference so far is the Decision LOV is the Controls Review Log Decision LOV
const COMPONENT_NAME = 'edit-controls-review-log-dialog';

const EditControlsReviewLogDialog = ({ reviewLog, onClose, onSubmit, studyRef }: Props) => {
    const { t } = useTranslation('cm');

    const [decisionLov, setDecisionLov] = useState<
        { id: number; name: string; groupedBy: string; disabled: boolean }[]
    >([]);

    // chip colour values
    const statusRAGChip = statusRAGChips.find((chip) => chip.text === reviewLog.color_direction_threshold);
    const ootRagChip = ootRAGChips.find((chip) => chip.text === reviewLog.oot_description);

    const { data: observation, isLoading: loadingObservation } = ObservationsApi.useGetDetails(
        studyRef || '',
        reviewLog.observation_id,
        ObservationType.Controls,
    );

    const { data: lovData, isLoading: loadingData } = OrganisationLovApi.useGet(
        studyRef ?? '',
        NamedItemType.ControlsReviewLogDecision,
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<EditReviewLogForm>({
        mode: 'all',
        defaultValues: {
            dataset: reviewLog.dataset ? new Date(reviewLog.dataset) : null,
            association: reviewLog.association ?? '',
            panel: reviewLog.panel ?? '',
            decision: reviewLog.decision ? { name: reviewLog.decision, id: reviewLog.decision_id } : null,
            comments: reviewLog.comments ?? '',
            created_date: reviewLog.created_date ? new Date(reviewLog.created_date) : null,
            created_by: reviewLog.created_by ?? '',
            updated_date: reviewLog.updatedDate ? new Date(reviewLog.updated_date) : null,
            updated_by: reviewLog.updated_by ?? '',
        },
    });

    useEffect(() => {
        if (lovData && !loadingData) {
            const decisionData = lovData
                .filter((y) => y && y.name)
                .sort((a, b) => {
                    // Sort active items first (true values before false values)
                    if (a?.active !== b?.active) {
                        return a?.active ? -1 : 1;
                    }

                    if (a.name && b.name) {
                        return a.name?.localeCompare(b.name);
                    }

                    return 0;
                })
                .map((data) => ({
                    name: data?.name ?? '',
                    id: data?.id ?? 0,
                    groupedBy: data?.active ? 'Active' : 'Inactive',
                    disabled: reviewLog.decision
                        ? reviewLog.decision.toLowerCase() === data.name?.toLowerCase()
                            ? false
                            : !data.active
                        : !data.active,
                    active: data?.active ?? false,
                }));

            setDecisionLov(decisionData);

            if (reviewLog.decision && reviewLog.decision_id) {
                const currentData = decisionData?.find(
                    (decisionDataList) => decisionDataList?.id === Number(reviewLog.decision_id),
                );
                if (currentData)
                    reset((currentDefaults) => {
                        return { ...currentDefaults, decision: currentData };
                    });
            }
        }
    }, [lovData]);

    const dialogTitle = `${t('common:edit')} ${
        reviewLog.type === 'System'
            ? t('reviewLogDialog.systemControlsTitle')
            : t('reviewLogDialog.manualControlsTitle')
    }: ${reviewLog.record_id}`;

    const primaryBtnLabel = t('common:update');
    const createdBy = `${moment(reviewLog?.created_date).format('DD-MMM-YYYY')} by ${reviewLog?.created_by}`;
    const updatedBy = `${moment(reviewLog?.updated_date).format('DD-MMM-YYYY')} by ${reviewLog?.updated_by}`;

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    return (
        <DraggableDialog
            dataTestId="edit-study-review-log-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            createdUpdatedInfo={<CreatedUpdatedInfo created={createdBy} updated={updatedBy} width={600} />}
            createdUpdatedBackgroundColour="gray"
            initialHeight={790}
            initialWidth={616}
            additionalInfo={
                <ObservationDetails
                    observation={observation}
                    observationLoading={loadingObservation}
                    type={ObservationType.Controls}
                />
            }
            additionalInfoWidth={361}
            additionalInfoBtnLabel={t('observation')}
            bodyPadding="none"
            setHeight="100%"
        >
            <div className={cn(style.editSiteObservationDialog, style.rightBorderStyle, 'k-w-full k-h-full')}>
                {/* /readonly header start  */}
                <div className={cn('', 'k-pl-md k-pr-md')}>
                    {/* ROW 1: y value / status */}
                    <div className={cn('k-d-flex k-flex-row k-pt-0.5 k-pb-1', editDialogStyles.borderBottom)}>
                        {/* COL left: y-axis */}
                        <div
                            className={cn(
                                '!k-basis-8/12 k-d-flex k-flex-col k-align-items-center',
                                editDialogStyles.borderRight,
                            )}
                        >
                            <div>{reviewLog.y_axis_title}</div>
                            {/* y-axis values */}
                            <div className={cn(reviewLog.previous_y_value ? 'k-gap-3' : '', 'k-d-flex')}>
                                {/* previous y value (if any) */}
                                <div className={cn(editDialogStyles.readonlyPanelText)}>
                                    {reviewLog.previous_y_value}
                                </div>
                                {/* direction indicator */}
                                {reviewLog.y_value_change_direction && (
                                    <div className={cn(editDialogStyles.readonlyPanelText)}>
                                        <FontAwesomeIcon
                                            fontSize="10px"
                                            icon={
                                                reviewLog.y_value_change_direction === '2'
                                                    ? ['far', 'hyphen']
                                                    : ['fass', 'triangle']
                                            }
                                            className={cn({
                                                [styles.sortAscIcon]: reviewLog.y_value_change_direction === '0',
                                                [`${styles.sortDescIcon} k-rotate-180`]:
                                                    reviewLog.y_value_change_direction === '1',
                                            })}
                                        />
                                    </div>
                                )}
                                {/* current y value */}
                                <div className={cn(editDialogStyles.readonlyPanelText)}>{reviewLog.y_value}</div>
                            </div>
                        </div>
                        {/* COL right: status */}
                        <div className="!k-basis-4/12 k-d-flex k-flex-col k-align-items-center">
                            <div>{t('status')}</div>
                            <div>
                                {reviewLog.color_direction_threshold && (
                                    <Chip
                                        style={{
                                            backgroundColor: statusRAGChip?.backgroundColor,
                                            fontSize: 12,
                                            cursor: 'default',
                                            width: 120,
                                            border: `1px solid ${statusRAGChip?.borderColor}`,
                                            textAlign: 'center',
                                            color: '#3E536E',
                                        }}
                                        rounded="large"
                                        value=""
                                    >
                                        <span className="k-chip-label center">
                                            {reviewLog.color_direction_threshold.toUpperCase()}
                                        </span>
                                    </Chip>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* /ROW 1: y value / status */}
                    {/* ROW 2: numerator / out of tolerance */}
                    <div className={cn('k-d-flex k-flex-row k-pt-2 k-pb-1', editDialogStyles.borderBottom)}>
                        {/* COL left: numerator */}
                        <div
                            className={cn(
                                '!k-basis-8/12 k-d-flex k-flex-col k-align-items-center',
                                editDialogStyles.borderRight,
                            )}
                        >
                            <div>{reviewLog.numerator}</div>
                            <div className={cn(editDialogStyles.readonlyPanelText)}>{reviewLog.numerator_events}</div>
                        </div>
                        {/* COL right: out of tolerance */}
                        <div className="!k-basis-4/12 k-d-flex k-flex-col k-align-items-center">
                            <div>{t('reviewLogDialog.outOfTolerance')}</div>
                            <div>
                                <Chip
                                    style={{
                                        backgroundColor: ootRagChip?.backgroundColor,
                                        fontSize: 12,
                                        cursor: 'default',
                                        width: 120,
                                        border: `1px solid ${ootRagChip?.borderColor}`,
                                        textAlign: 'center',
                                        color: '#3E536E',
                                    }}
                                    rounded="large"
                                    value=""
                                >
                                    <span className="k-chip-label center">
                                        {reviewLog.oot_description?.toUpperCase()}
                                    </span>
                                </Chip>
                            </div>
                        </div>
                    </div>
                    {/* /ROW 2: numerator / out of tolerance */}
                    {/* ROW 3: subject weeks / tolerance limit */}
                    <div className="k-d-flex k-flex-row k-pt-2 k-pb-1">
                        {/* COL left: subject weeks */}
                        <div
                            className={cn(
                                '!k-basis-8/12 k-d-flex k-flex-col k-align-items-center',
                                editDialogStyles.borderRight,
                            )}
                        >
                            <div>{reviewLog.denominator}</div>
                            <div className={cn(editDialogStyles.readonlyPanelText)}>{reviewLog.denominator_count}</div>
                        </div>

                        {/* COL right - tolerance limit */}
                        <div className="!k-basis-4/12 k-d-flex k-flex-col k-align-items-center">
                            <div>{t('reviewLogDialog.toleranceLimit')}</div>
                            <div className={cn(editDialogStyles.readonlyPanelText)}>
                                {reviewLog.out_of_tolerance_value}
                            </div>
                        </div>
                    </div>
                    {/* /ROW 3: numerator / out of tolerance */}
                    {/* /readonly header end */}
                </div>

                {/* readonly panel, dataset and calculation info */}
                <div
                    className={cn(
                        'k-pl-md k-pr-md k-d-grid k-align-items-baseline k-gap-2 k-pt-sm k-pb-sm',
                        style.editSiteObservationInfo,
                        editDialogStyles.borderBottom,
                        editDialogStyles.borderTop,
                    )}
                >
                    <div className={cn(style.pipeGap, 'k-d-flex')}>
                        <div className="k-d-flex k-align-items-center !k-basis-8/12 k-gap-13">
                            <div className="k-font-medium">{t('reviewLogDialog.type')}</div>
                            <div className={cn(editDialogStyles.readonlyPanelText, 'k-pl-1')}>{reviewLog.type}</div>
                        </div>
                        <div className="k-d-flex k-align-items-center !k-basis-6/12 k-gap-2">
                            <div className="k-font-medium">{t('reviewLogDialog.numberOfActions')}</div>
                            <div className={cn(editDialogStyles.readonlyPanelText)}>{reviewLog.actions_count}</div>
                        </div>
                        <div className="k-d-flex k-align-items-center !k-basis-7/12 k-gap-2">
                            <div className="k-font-medium">{t('reviewLogDialog.dataset')}</div>
                            <div className={cn(editDialogStyles.readonlyPanelText)}>{reviewLog.dataset}</div>
                        </div>
                    </div>

                    <div className="k-d-flex k-gap-13">
                        <div className="k-font-medium">{t('reviewLogDialog.panel')}</div>
                        <div className={cn(editDialogStyles.readonlyPanelText)}>
                            {reviewLog.panel}
                            {reviewLog.panel_active?.toLowerCase() === 'no' && (
                                <div className={cn(editDialogStyles.panelInactiveIcon, 'k-pl-sm')}>
                                    <InactiveIcon
                                        toolTipText={t('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={cn(style.pipeGap, 'k-shrink-0 k-d-flex k-flex k-gap-8')}>
                        <div className="k-d-flex k-gap-2 k-basis-1/3">
                            <div className="k-font-medium">{t('reviewLogDialog.timePeriod')}</div>
                            <div className={cn(editDialogStyles.readonlyPanelText, 'k-pl-1')}>
                                {reviewLog.time_period}
                            </div>
                        </div>
                        <div className="k-d-flex k-gap-2 k-align-items-center">
                            <div className="k-font-medium">{t('reviewLogDialog.displayType')}</div>
                            <div className={cn(editDialogStyles.readonlyPanelText)}>
                                {reviewLog.cumulative_or_time_period}
                            </div>
                        </div>
                    </div>
                    <div className="k-d-flex k-gap-2">
                        <div className="k-font-medium">{t('reviewLogDialog.scaleFactor')}</div>
                        <div className={cn(editDialogStyles.readonlyPanelText)}>{reviewLog.scale_by}</div>
                    </div>
                </div>

                <form id="edit-study-review-log" className="k-w-full k-pt-sm">
                    <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-sm k-pl-md k-pr-md">
                        <BasicDropDownController
                            label={t('reviewLogDialog.decision')}
                            control={control}
                            name="decision"
                            data-testid="edit-review-log-decision"
                            data={decisionLov}
                            textField="name"
                            groupField="groupedBy"
                            dataItemKey="id"
                            filterable
                            loading={loadingData}
                            allowClear
                            activeField="active"
                            readOnly={isSubmitting}
                        />
                    </div>
                    <div className="k-d-flex k-flex-auto k-justify-content-center k-pl-md k-pr-md">
                        <TextAreaController
                            maxLength={3000}
                            control={control}
                            name="comments"
                            data-testid="edit-review-log-comments"
                            label={t('reviewLogDialog.comments')}
                            style={{
                                height: '326px',
                                overflow: 'auto',
                            }}
                            readOnly={isSubmitting}
                        />
                    </div>
                </form>
            </div>
        </DraggableDialog>
    );
};

export default EditControlsReviewLogDialog;
